import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { FaTrash } from 'react-icons/fa';
import { jwtDecode } from 'jwt-decode';

const homeByRole = (rol) => {
  if (rol === 'porteria') return '/porteria-turnos';
  if (rol === 'admin') return '/admin-turnos';
  return '/dashboard';
};

const HIDDEN_KEY = 'dashboard.hiddenTurnoIds';

const Dashboard = () => {
  const navigate = useNavigate();
  const [turnos, setTurnos] = useState([]);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [expandedRows, setExpandedRows] = useState([]);
  const [hiddenIds, setHiddenIds] = useState(() => {
    try {
      const raw = localStorage.getItem(HIDDEN_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(arr) ? arr : []);
    } catch {
      return new Set();
    }
  });
  const [mostrarOcultos, setMostrarOcultos] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(HIDDEN_KEY, JSON.stringify([...hiddenIds]));
    } catch {
      // no-op
    }
  }, [hiddenIds]);

  const rol = useMemo(() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      return jwtDecode(token)?.rol || null;
    } catch {
      return null;
    }
  }, []);

  const fetchMisTurnos = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/turnos/mis-turnos', {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = Array.isArray(response.data) ? response.data : [];
      if (data.length === 0) {
        setTurnos([]);
        setError('No tienes turnos registrados.');
        return;
      }

      const turnosParseados = data.map((turno) => {
        let parsedBultos = [];
        try {
          parsedBultos = Array.isArray(turno.proveedor_bultos)
            ? turno.proveedor_bultos
            : typeof turno.proveedor_bultos === 'string'
              ? JSON.parse(turno.proveedor_bultos)
              : [];
        } catch {
          parsedBultos = [];
        }

        return {
          ...turno,
          proveedor_bultos: parsedBultos,
          fecha_turno: turno.fecha_turno ? new Date(`${turno.fecha_turno}T00:00:00`) : null,
        };
      });

      setTurnos(turnosParseados);
      setError('');
    } catch (err) {
      console.error('Error al obtener turnos:', err);
      const status = err?.response?.status;
      if (status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/', { replace: true });
        return;
      }
      if (status === 403) {
        navigate(homeByRole(rol), { replace: true });
        return;
      }
      setError('Error al obtener los turnos.');
    }
  }, [navigate, rol]);

  useEffect(() => {
    if (!rol) return;
    if (rol !== 'transportista' && rol !== 'proveedor') {
      navigate(homeByRole(rol), { replace: true });
    }
  }, [rol, navigate]);

  useEffect(() => {
    if (rol === 'transportista' || rol === 'proveedor') {
      fetchMisTurnos();
    } else {
      setTurnos([]);
      setError('');
    }
  }, [rol, fetchMisTurnos]);

  const toggleExpand = (turnoId) => {
    setExpandedRows((prev) =>
      prev.includes(turnoId) ? prev.filter((id) => id !== turnoId) : [...prev, turnoId]
    );
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleSolicitarTurno = () => {
    if (rol === 'proveedor') return navigate('/solicitar-turno-proveedor');
    if (rol === 'transportista') return navigate('/solicitar-turno');
  };

  const handleConfirmarTurno = async (turnoId, confirmado) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.put(
        `/turnos/${turnoId}/confirmar`,
        { confirmado: Boolean(confirmado) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMensaje(response.data.mensaje);
      setTimeout(() => setMensaje(''), 3000);
      fetchMisTurnos();
    } catch (err) {
      console.error('Error al actualizar turno:', err);
      setError(err.response?.data?.error || 'Error al actualizar el turno.');
    }
  };

  const handleOcultarTurno = (turnoId) => {
    if (!window.confirm('Quieres ocultar este turno de tu Dashboard?')) return;
    setHiddenIds((prev) => {
      const next = new Set(prev);
      next.add(turnoId);
      return next;
    });
    setMensaje('Turno ocultado en tu Dashboard.');
    setTimeout(() => setMensaje(''), 3000);
  };

  const handleRestaurarTurno = (turnoId) => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      next.delete(turnoId);
      return next;
    });
    setMensaje('Turno restaurado en tu Dashboard.');
    setTimeout(() => setMensaje(''), 3000);
  };

  const turnosFiltrados = turnos.filter(
    (t) =>
      (!filtroEstado || t.estado === filtroEstado) &&
      (mostrarOcultos || !hiddenIds.has(t.id))
  );

  const resumen = useMemo(() => {
    const pendientes = turnos.filter((turno) => turno.estado === 'pendiente').length;
    const aprobados = turnos.filter((turno) => turno.estado === 'aprobado').length;
    const conRespuesta = turnos.filter(
      (turno) => turno.estado === 'pendiente_confirmacion_usuario'
    ).length;

    return {
      total: turnos.length,
      pendientes,
      aprobados,
      conRespuesta,
    };
  }, [turnos]);

  const renderFecha = (f) => {
    if (!f) return '-';
    try {
      const d = f instanceof Date ? f : new Date(`${f}T00:00:00`);
      return d.toLocaleDateString();
    } catch {
      return String(f);
    }
  };

  const renderMotivo = (turno) => {
    const debe = ['rechazado', 'pendiente_confirmacion_usuario'].includes(turno.estado);
    if (!debe) return '-';
    const texto = (turno.devolucion || '').trim();
    return texto ? (
      <span
        title={texto}
        style={{
          maxWidth: 280,
          display: 'inline-block',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {texto}
      </span>
    ) : (
      '-'
    );
  };

  return (
    <div className="page-shell">
      <section className="page-hero">
        <div className="page-hero__content">
          <div>
            <p className="page-hero__eyebrow">Seguimiento personal</p>
            <h2 className="page-hero__title">Mis turnos</h2>
            <p className="page-hero__subtitle">
              Revisa tus solicitudes, confirma cambios pendientes y mantene visible el estado
              operativo de tus ingresos.
            </p>
          </div>
          <div className="page-hero__meta">
            <span className="hero-chip">{resumen.total} registros</span>
            <span className="hero-chip">{resumen.conRespuesta} con accion pendiente</span>
          </div>
        </div>
      </section>

      <section className="dashboard-grid">
        <div className="metric-card" style={{ gridColumn: 'span 3' }}>
          <p className="metric-card__label">Total de turnos</p>
          <p className="metric-card__value">{resumen.total}</p>
          <p className="metric-card__hint">Vista consolidada de tus solicitudes.</p>
        </div>
        <div className="metric-card" style={{ gridColumn: 'span 3' }}>
          <p className="metric-card__label">Pendientes</p>
          <p className="metric-card__value">{resumen.pendientes}</p>
          <p className="metric-card__hint">Esperando revision o definicion.</p>
        </div>
        <div className="metric-card" style={{ gridColumn: 'span 3' }}>
          <p className="metric-card__label">Aprobados</p>
          <p className="metric-card__value">{resumen.aprobados}</p>
          <p className="metric-card__hint">Listos para operar en fecha y hora.</p>
        </div>
        <div className="metric-card" style={{ gridColumn: 'span 3' }}>
          <p className="metric-card__label">Con cambios a confirmar</p>
          <p className="metric-card__value">{resumen.conRespuesta}</p>
          <p className="metric-card__hint">Requieren tu respuesta desde esta vista.</p>
        </div>
      </section>

      <section className="surface-card">
        <div className="toolbar-stack toolbar-stack--split">
          <div className="toolbar-cluster">
            <button className="btn btn-primary" onClick={handleSolicitarTurno}>
              Solicitar turno
            </button>
            <button className="btn btn-outline-secondary" onClick={fetchMisTurnos}>
              Refrescar
            </button>
          </div>
          <button className="btn btn-danger" onClick={handleLogout}>
            Cerrar sesion
          </button>
        </div>

        {mensaje && <div className="alert alert-success mt-4 mb-0">{mensaje}</div>}
        {error && <div className="alert alert-warning mt-4 mb-0">{error}</div>}

        <div className="row g-3 mt-2">
          <div className="col-md-6">
            <label className="form-label">Filtrar por estado</label>
            <select
              className="form-select"
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="pendiente">Pendiente</option>
              <option value="pendiente_confirmacion_usuario">Pendiente confirmacion</option>
              <option value="aprobado">Aprobado</option>
              <option value="rechazado">Rechazado</option>
            </select>
          </div>
          <div className="col-md-6 d-flex align-items-end">
            <div className="form-check">
              <input
                className="form-check-input"
                id="chkMostrarOcultos"
                type="checkbox"
                checked={mostrarOcultos}
                onChange={() => setMostrarOcultos((v) => !v)}
              />
              <label className="form-check-label ms-2" htmlFor="chkMostrarOcultos">
                Mostrar turnos ocultos
              </label>
            </div>
          </div>
        </div>
      </section>

      <section className="table-card">
        <div className="table-card__header">
          <div>
            <h3 className="table-card__title">Detalle operativo</h3>
            <p className="table-card__subtitle">
              {turnosFiltrados.length} turno(s) visibles segun tus filtros actuales.
            </p>
          </div>
        </div>

        {turnosFiltrados.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-modern align-middle text-center">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Hora desde</th>
                  <th>Estado</th>
                  <th>Observaciones</th>
                  <th>Motivo / devolucion</th>
                  <th>Acciones</th>
                  <th>Detalle</th>
                </tr>
              </thead>
              <tbody>
                {turnosFiltrados.map((turno) => {
                  const estaOculto = hiddenIds.has(turno.id);
                  return (
                    <tr key={turno.id} className={estaOculto ? 'table-secondary' : ''}>
                      <td>{renderFecha(turno.fecha_turno)}</td>
                      <td>{turno.hora_desde || '-'}</td>
                      <td>
                        <span
                          className={`badge ${
                            turno.estado === 'aprobado'
                              ? 'bg-success'
                              : turno.estado === 'rechazado'
                                ? 'bg-danger'
                                : turno.estado === 'pendiente_confirmacion_usuario'
                                  ? 'bg-info'
                                  : 'bg-warning'
                          }`}
                        >
                          {turno.estado === 'pendiente_confirmacion_usuario'
                            ? 'Pendiente confirmacion'
                            : turno.estado}
                        </span>
                        {estaOculto && <span className="ms-2 badge bg-dark">Oculto</span>}
                      </td>
                      <td>{turno.observaciones || 'Sin observaciones'}</td>
                      <td>{renderMotivo(turno)}</td>
                      <td>
                        {turno.devolucion &&
                          turno.devolucion.trim() &&
                          turno.devolucion !== 'Sin devolucion' &&
                          (turno.estado === 'pendiente' ||
                            turno.estado === 'pendiente_confirmacion_usuario') &&
                          turno.confirmado_por_usuario !== true && (
                            <div className="d-flex justify-content-center flex-wrap gap-2 mb-2">
                              <button
                                className="btn btn-success btn-sm"
                                onClick={() => handleConfirmarTurno(turno.id, true)}
                              >
                                Aceptar
                              </button>
                              <button
                                className="btn btn-warning btn-sm"
                                onClick={() => handleConfirmarTurno(turno.id, false)}
                              >
                                Rechazar
                              </button>
                            </div>
                          )}

                        {turno.estado !== 'pendiente' &&
                          turno.estado !== 'pendiente_confirmacion_usuario' && (
                            <>
                              {!estaOculto ? (
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => handleOcultarTurno(turno.id)}
                                  title="Ocultar de mi Dashboard"
                                >
                                  <FaTrash /> <span className="ms-1">Ocultar</span>
                                </button>
                              ) : (
                                <button
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={() => handleRestaurarTurno(turno.id)}
                                  title="Volver a mostrar en mi Dashboard"
                                >
                                  Restaurar
                                </button>
                              )}
                            </>
                          )}
                      </td>

                      <td className="text-start">
                        <button
                          className="btn btn-sm btn-outline-secondary mb-2"
                          onClick={() => toggleExpand(turno.id)}
                        >
                          {expandedRows.includes(turno.id) ? 'Ocultar detalle' : 'Ver detalle'}
                        </button>
                        {expandedRows.includes(turno.id) && (
                          <>
                            {Array.isArray(turno.proveedor_bultos) &&
                            turno.proveedor_bultos.length > 0 ? (
                              turno.proveedor_bultos.map((p, i) => (
                                <div key={i} className="mb-3 border-top pt-2">
                                  <strong>Proveedor:</strong> {p.nombre || 'No informado'} <br />
                                  <strong>Remito:</strong> {p.remito || 'No informado'} <br />
                                  <strong>Orden de compra:</strong>{' '}
                                  {p.orden_compra || 'No informada'} <br />
                                  <strong>Facturas:</strong>{' '}
                                  {Array.isArray(p.facturas) && p.facturas.length > 0
                                    ? p.facturas.join(', ')
                                    : 'No hay facturas'}
                                  <br />
                                  <strong>Bultos:</strong>{' '}
                                  {p.bultos && Object.keys(p.bultos).length > 0 ? (
                                    <ul className="mb-0 ps-3">
                                      {Object.entries(p.bultos).map(([tipo, cantidad]) =>
                                        cantidad > 0 ? (
                                          <li key={tipo}>
                                            {tipo}: {cantidad}
                                          </li>
                                        ) : null
                                      )}
                                    </ul>
                                  ) : (
                                    'No hay bultos'
                                  )}
                                </div>
                              ))
                            ) : (
                              <div className="text-muted">No hay proveedores asignados</div>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <p className="empty-state__title">No hay turnos para mostrar</p>
            <p className="empty-state__text">
              Ajusta los filtros o crea una nueva solicitud para comenzar.
            </p>
          </div>
        )}
      </section>
    </div>
  );
};

export default Dashboard;
